import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Scale, Loader2, Plus, Check, X, PlayCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CURRENCIES = ["EUR", "USD", "TRY", "GBP"];
const STATUS_STYLE = { pending: "bg-amber-100 text-amber-700", approved: "bg-blue-100 text-blue-700", executed: "bg-emerald-100 text-emerald-700", rejected: "bg-slate-200 text-slate-500" };
const money = (a, c) => new Intl.NumberFormat("en-IE", { style: "currency", currency: c || "EUR" }).format(a || 0);

export default function Approvals() {
  const { can } = useAuth();
  const t = useT();
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [patientFilter, setPatientFilter] = useState("all");
  const [form, setForm] = useState({ patient_id: "", kind: "discount", amount: "", currency: "EUR", reason: "" });
  const canCreate = can("discounts_refunds", "create");
  const canApprove = can("discounts_refunds", "approve");
  const canExecute = can("discounts_refunds", "execute");

  const load = useCallback(() => {
    setLoading(true);
    const q = [];
    if (statusFilter !== "all") q.push(`status=${statusFilter}`);
    if (patientFilter !== "all") q.push(`patient_id=${patientFilter}`);
    api.get(`/approvals${q.length ? "?" + q.join("&") : ""}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [statusFilter, patientFilter]);
  useEffect(() => { load(); api.get("/patients").then((r) => setPatients(r.data)).catch(() => {}); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_id || !form.amount) return toast.error(t("approvals.requiredFields"));
    try { await api.post("/approvals", { ...form, amount: Number(form.amount) }); toast.success(t("approvals.requestSubmitted")); setOpen(false); setForm({ patient_id: "", kind: "discount", amount: "", currency: "EUR", reason: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const act = async (a, action) => {
    const doneKey = action === "approve" ? "s_approved" : action === "reject" ? "s_rejected" : "s_executed";
    try { await api.post(`/approvals/${a.id}/${action}`); toast.success(`${t("approvals.request")} ${t(`approvals.${doneKey}`)}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1100px]">
      <PageHeader title={t("approvals.title")} subtitle={t("approvals.subtitle")}>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "pending", "approved", "executed", "rejected"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? t("approvals.allStatuses") : t(`approvals.s_${s}`, s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={patientFilter} onValueChange={setPatientFilter}>
          <SelectTrigger className="w-44" data-testid="filter-patient"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("approvals.allPatients")}</SelectItem>
            {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}
          </SelectContent>
        </Select>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-approval-button" className="bg-navy hover:bg-navy-soft text-white gap-2"><Plus className="h-4 w-4" />{t("approvals.request")}</Button></DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading">{t("approvals.newRequest")}</DialogTitle><DialogDescription>{t("approvals.newRequestDesc")}</DialogDescription></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-3 mt-2" data-testid="approval-form">
                <div className="space-y-1.5 col-span-2"><Label>{t("approvals.patient")}</Label>
                  <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                    <SelectTrigger data-testid="approval-patient"><SelectValue placeholder={t("approvals.patient")} /></SelectTrigger>
                    <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.patient_code} · {p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>{t("approvals.type")}</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}><SelectTrigger data-testid="approval-kind"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="discount">{t("approvals.discount")}</SelectItem><SelectItem value="refund">{t("approvals.refund")}</SelectItem></SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label>{t("approvals.currency")}</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1.5 col-span-2"><Label>{t("approvals.amount")}</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="approval-amount" /></div>
                <div className="space-y-1.5 col-span-2"><Label>{t("approvals.reason")}</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
                <DialogFooter className="col-span-2 mt-2"><Button type="submit" data-testid="approval-submit" className="bg-navy hover:bg-navy-soft text-white">{t("approvals.submitRequest")}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>
        : items.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 p-16 text-center text-slate-400"><Scale className="h-6 w-6 mx-auto mb-2 text-slate-300" />{t("approvals.noRequests")}</div>
        : (
          <div className="space-y-3" data-testid="approval-list">
            {items.map((a) => (
              <div key={a.id} data-testid={`approval-${a.id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-medium">{t(`approvals.${a.kind}`, a.kind)} · {money(a.amount, a.currency)} <span className="text-xs text-slate-400 font-normal">· {a.patient_name}</span></p>
                  <p className="text-sm text-slate-500">{t("approvals.requestedBy")} {a.requested_by}{a.approved_by ? ` · ${t("approvals.approvedBy")} ${a.approved_by}` : ""}{a.executed_by ? ` · ${t("approvals.executedBy")} ${a.executed_by}` : ""}</p>
                  {a.reason && <p className="text-xs text-slate-400">{a.reason}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={`${STATUS_STYLE[a.status]} font-normal`}>{t(`approvals.s_${a.status}`, a.status)}</Badge>
                  {a.status === "pending" && canApprove && (<>
                    <Button size="sm" onClick={() => act(a, "approve")} data-testid={`approve-${a.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"><Check className="h-4 w-4" />{t("approvals.approve")}</Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => act(a, "reject")} data-testid={`reject-${a.id}`}><X className="h-4 w-4" /></Button>
                  </>)}
                  {a.status === "approved" && canExecute && (
                    <Button size="sm" onClick={() => act(a, "execute")} data-testid={`execute-${a.id}`} className="bg-navy hover:bg-navy-soft text-white gap-1"><PlayCircle className="h-4 w-4" />{t("approvals.execute")}</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
